/**
 * A munkalista állapota: a betöltés, a SORFÓKUSZ és a SOR-MÓDÚ mentés.
 *
 * UGYANAZ A HÁROM MINTA, mint a hiánylistán, és szándékosan:
 *
 *   • a horog csak KIADJA a rövidítéseit, a `Shell` fűzi őket egy
 *     regiszterbe — különben a súgó ketté hasadna (`useShortcuts.ts`);
 *   • a fókusz AZONOSÍTÓN ül, nem indexen — egy frissítés átrendezheti a
 *     sorokat, és egy indexre kötött fókusz más bizonylatra ugrana;
 *   • a betöltésnek GENERÁCIÓJA van — egy lassú hibaválasz nem írhatja felül
 *     a friss listát.
 *
 * AMI ITT TÖBB: a lefűzés két dolgot választ ki, nem egyet. A bizonylatot ÉS
 * a hiánytételt, amit lezár. Ezért van `focusedId` és `candidate` is — a
 * kettő külön kurzor, és az `Enter` a kettő METSZETÉN dolgozik.
 *
 * ═══ A SOR-MÓD (2.6/22) ═══
 *
 * **Optimista mentés, visszagörgetéssel.** Az `Enter` azonnal továbblép; a
 * mentés a háttérben megy. Ez négy dolgot tesz kötelezővé, és nélkülük fél
 * döntés — mind a négy ITT van, mert mind a négy ÁLLAPOT, nem megjelenítés:
 *
 *   1. **Előellenőrzés** a továbblépés előtt (`blockFor`) — a determinisztikus
 *      visszautasítás nem a visszagörgetésen megy át.
 *   2. **Több EGYIDEJŰ bukás.** A `pending` és a `failed` LISTA, nem egyetlen
 *      állapotmező. Egy három másodperces kiesés nyolc-tíz sort visz
 *      magával; egyetlen bukásra tervezett visszagörgetés pont a rossz napon
 *      lenne használhatatlan.
 *   3. **A bukás elmulaszthatatlan.** Nem eltűnő buborék: a `failed` sor ott
 *      marad, amíg a könyvelő nem intézi el, és amíg van ilyen, a `blocked`
 *      igaz — a listából nem lehet kilépni.
 *   4. **A válasz a HELYES sorhoz kötődik.** A `documentId` megy végig a
 *      záradékon, nem egy index — egy későn érkező hibaválasz így sem tud
 *      másik bizonylatot megjelölni.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/features/auth/AuthProvider';
import type { DiscrepancyListItem } from '@/features/discrepancies/types';
import type { Shortcut } from '@/hooks/useShortcuts';
import { ApiError, api } from '@/lib/api-client';
import { dueNow, type PendingExclusion, stillWaiting, UNDO_WINDOW_MS } from './exclusion';
import { groupByCompany } from './queueGroups';
import { blockFor, type FilingBlock, idAfterRemoving, removeItem, restoreItem } from './rowMode';
import { filterItems } from './search';
import type { FilingResult, Worklist, WorklistItem } from './types';

export type WorklistStatus = 'loading' | 'failed' | 'forbidden' | 'ready';

/**
 * EGY BUKOTT MENTÉS. A `reason` két ág, mert a könyvelő mást tud velük
 * kezdeni: a `conflict` (409) azt jelenti, hogy a tétel időközben
 * megváltozott — az újrapróbálás ugyanazzal az adattal megint elbukna, a
 * listát kell frissíteni. A `failed` viszont újrapróbálható.
 */
export interface FailedFiling {
  documentId: string;
  discrepancyId: string;
  /** A sor, ahogy eltűnt — ebből áll vissza a kupacban. */
  item: WorklistItem;
  reason: 'conflict' | 'failed';
}

export interface WorklistState {
  status: WorklistStatus;
  list: Worklist | null;
  /**
   * A LEGUTÓBBI SZERVERES LISTA KÉRÉSÉNEK INDULÁSA (`Date.now()`), vagy `null`,
   * ha még nem jött. A `list` a helyi lefűzéskor és kizáráskor is új objektum
   * — ez csak akkor változik, amikor a SZERVER adott friss listát. A „kétes
   * kötés" jelvény helyi levétele ehhez igazodik (#54): ami ennél korábbi
   * döntés, arról a lista már tud.
   */
  loadedSince: number | null;
  /**
   * AMI LÁTSZIK — a keresés által szűkített kupac.
   *
   * Külön a `list`-től, mert a FEJLÉC SZÁMA a teljes kupacé marad: a
   * „Elintézetlen · 12" nem attól lesz kevesebb, hogy a könyvelő beírt egy
   * szót. A szűrés a látómezőt szűkíti, nem a munkát.
   */
  visible: WorklistItem[];
  /** A keresés szövege. */
  query: string;
  setQuery: (value: string) => void;
  /**
   * A `/` KÉRÉSE: vidd a fókuszt a keresőbe. A mező mindig ott áll (#68);
   * minden leütés ÚJ objektum, mert a második `/` is oda kell vigyen.
   */
  searchRequest: object | null;
  /** `Esc` a keresőben: a szűrő törlődik, a fókusz vissza a kupac sorára. */
  leaveSearch: () => void;
  /**
   * A `→` KÉRÉSE: lépj be a kijelölt bizonylatba — a fókusz a papírra (#68).
   * A papír dobozát a horog nem ismeri, ezért kérés, nem művelet.
   */
  enterRequest: object | null;
  /** Vissza a kupacra — a kijelölt sorra (`←` a papír szélén, `Esc`). */
  focusQueue: () => void;
  reload: () => void;
  /**
   * CSENDES FRISSÍTÉS — a lista helyben marad, csak az adata cserélődik. A
   * friss Tensoft-adat érkezésekor fut (#54, Viktor 10-05: „magától"): a
   * `reload` „Betöltés…"-re cserélné a listát, és a könyvelő elveszítené a
   * helyét és a fókuszt egy olyan frissítés miatt, amit nem ő kért.
   */
  refresh: () => void;
  focusedId: string | null;
  /**
   * ÁTÁLLÁS EGY MÁSIK BIZONYLATRA — nem nyers setter.
   *
   * A `setFocusedId` szándékosan nincs kiadva: a bizonylat váltása HÁROM
   * dolgot jelent, nem egyet. Aki csak a fókuszt írja át, ott hagyja az
   * ELŐZŐ bizonylathoz választott hiánytételt, és a „Lefűzöm" engedélyezett
   * marad — egy másik cég tételére. A szerver visszautasítaná, de a
   * felületnek nem szabad odáig elengednie.
   */
  selectDocument: (id: string) => void;
  registerRow: (id: string, element: HTMLElement | null) => void;
  /** Melyik hiánytételt zárná le a kijelölt bizonylat — a TELJES sor. */
  candidate: DiscrepancyListItem | null;
  candidateId: string | null;
  setCandidate: (candidate: DiscrepancyListItem | null) => void;
  /**
   * A hiánytételek, amiket a sor-mód már lezárt — optimistán vagy
   * megerősítve. A jelöltek közül ki kell esniük, különben a könyvelő
   * ugyanazt a tételt még egyszer felkínálva látná, és a második `Enter`
   * 409-cel bukna. Visszagörgetéskor a tétel visszakerül a halmazból.
   */
  settledDiscrepancyIds: ReadonlySet<string>;
  /** Az előellenőrzés kifogása, ha az `Enter` nem lépett tovább. */
  block: FilingBlock | null;
  /** Hány mentés van a levegőben. */
  pendingCount: number;
  failed: FailedFiling[];
  /** Elintézetlen bukással a listából nem lehet kilépni (2.6/22, 3. pont). */
  blocked: boolean;
  /** `Enter` — mentés és következő. */
  fileAndAdvance: () => void;
  /** `X` — nem elszámolható, visszavonási ablakkal. */
  exclude: () => void;
  /** Amit még vissza lehet vonni. */
  pendingExclusions: PendingExclusion[];
  undoExclusion: (documentId: string) => void;
  retryFailed: (documentId: string) => void;
  discardFailed: (documentId: string) => void;
  focusFailed: () => void;
  /** A hiánylista újratöltése után a helyi lezárás-nyilvántartás elévül. */
  clearSettled: () => void;
  shortcuts: Shortcut[];
}

export function useWorklist(): WorklistState {
  const { signOut } = useAuth();
  const [list, setList] = useState<Worklist | null>(null);
  const [loadedSince, setLoadedSince] = useState<number | null>(null);
  const [status, setStatus] = useState<WorklistStatus>('loading');
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [candidate, setCandidateRaw] = useState<DiscrepancyListItem | null>(null);
  const [block, setBlock] = useState<FilingBlock | null>(null);
  const [pending, setPending] = useState<string[]>([]);
  const [failed, setFailed] = useState<FailedFiling[]>([]);
  const [settled, setSettled] = useState<ReadonlySet<string>>(() => new Set());
  /**
   * A KIZÁRÁSOK, AMIK MÉG VISSZAVONHATÓK.
   *
   * A kérés CSAK az ablak leteltével indul el — a levezetés az
   * `exclusion.ts` fejlécében. Amíg itt áll, a szerver nem tud róla.
   */
  const [excluding, setExcluding] = useState<PendingExclusion[]>([]);
  const [query, setQuery] = useState('');
  const [searchRequest, setSearchRequest] = useState<object | null>(null);
  const [enterRequest, setEnterRequest] = useState<object | null>(null);
  const rows = useRef(new Map<string, HTMLElement>());
  const generation = useRef(0);
  /**
   * HÁNY ELINTÉZETLEN BUKÁS VOLT AZ ELŐZŐ RENDERBEN — a fókusz-visszaugrás
   * ÁTMENETRE szól, nem állapotra. Enélkül az effekt minden renderben
   * visszarántaná a kurzort, amíg egyetlen bukás áll: a könyvelő nem tudna
   * elmozdulni róla.
   */
  const failedBefore = useRef(0);

  /** Az utolsó renderelt állapot — a csendes frissítés ebből dönt. */
  const statusNow = useRef(status);
  // Effektben, nem a renderben — a `useNavSync` `finished` ref-jének érvelésével.
  useEffect(() => {
    statusNow.current = status;
  });

  /**
   * A HELYBEN MÁR LEVETT SOROK — a futó lefűzés (`pending`) és a még
   * visszavonható kizárás (`excluding`). A szerver ezekről még nem tud, tehát
   * a friss listában ott vannak; visszaengedve a sor újra lefűzhető lenne
   * (409), visszavonáskor pedig kétszer állna a kupacban. A csendes frissítés
   * óta ez magától is bekövetkezhet, nem csak a könyvelő kérésére
   * (review, 10-05).
   */
  const hidden = useRef<ReadonlySet<string>>(new Set());
  useEffect(() => {
    hidden.current = new Set([...pending, ...excluding.map((entry) => entry.documentId)]);
  });

  const fetchList = useCallback(
    (quiet: boolean) => {
      const mine = generation.current + 1;
      generation.current = mine;
      const startedAt = Date.now();
      if (!quiet) setStatus('loading');

      void api<Worklist>('/documents/worklist')
        .then((loaded) => {
          if (mine !== generation.current) {
            return;
          }
          setList([...hidden.current].reduce(removeItem, loaded));
          setLoadedSince(startedAt);
          setStatus('ready');
        })
        .catch((caught: unknown) => {
          // A CSENDES FRISSÍTÉS HIBÁJA NEM DOBJA EL A LISTÁT. A régi adat
          // továbbra is igaz arra, amit mutat — egy hibaüzenet a munka helyén
          // többet ártana, mint egy kimaradt frissítés. A 401 viszont az.
          if (mine !== generation.current || (quiet && !isUnauthorized(caught))) {
            return;
          }

          // A SZERVER ÜZENETÉT NEM MUTATJUK — ugyanaz az érvelés, mint a
          // hiánylistán: egy listalekérés hibája a RENDSZERRŐL szól, nem a
          // felhasználó bemenetéről.
          const outcome = failureOf(caught);
          if (outcome === 'expired') {
            signOut('expired');
            return;
          }
          setStatus(outcome);
        });
    },
    [signOut]
  );

  const load = useCallback(() => fetchList(false), [fetchList]);
  // ⚠️ CSAK KÉSZ LISTÁN CSENDES. Egy futó betöltés közben a csendes kérés
  // elvenné a generációt, a betöltés eredménye eldobódna, és a lista örökre
  // „Betöltés…"-en állna. Ilyenkor a rendes betöltés fut le újra.
  const refresh = useCallback(() => fetchList(statusNow.current === 'ready'), [fetchList]);

  useEffect(() => {
    load();
    return () => {
      generation.current += 1;
    };
  }, [load]);

  /**
   * A SZŰRT KUPAC — és MINDEN, ami sorrend, EBBŐL jön.
   *
   * A `J`/`K` és az `Enter` utáni továbblépés a LÁTHATÓ sorok közt visz; a
   * teljes listából számolva a kurzor elrejtett sorokra ugrana, és a könyvelő
   * nem értené, hova tűnt. A keresés a MUNKA SORRENDJÉT is átírja, nem csak
   * a megjelenítést — ez a `/` értelme.
   */
  // A LÁTHATÓ SOROK CÉGENKÉNT (#68): a sor-mód is ezen a sorrenden jár.
  const visible = useMemo(
    () =>
      list ? groupByCompany(filterItems(list.items, query)).flatMap((group) => group.items) : [],
    [list, query]
  );

  const order = useMemo(() => visible.map((item) => item.id), [visible]);

  const registerRow = useCallback((id: string, element: HTMLElement | null) => {
    if (element) {
      rows.current.set(id, element);
    } else {
      rows.current.delete(id);
    }
  }, []);

  const setCandidate = useCallback((next: DiscrepancyListItem | null) => {
    setCandidateRaw(next);
    // ÚJ JELÖLT, ÚJ ESÉLY. A korábbi kifogás egy MÁSIK tételről szólt; ott
    // hagyva a könyvelő azt hinné, hogy a most választott is hibás.
    setBlock(null);
  }, []);

  const selectDocument = useCallback(
    (id: string) => {
      setFocusedId(id);
      // MÁSIK BIZONYLAT, MÁSIK JELÖLT. A hiánytétel-kijelölés az ELŐZŐ
      // bizonylathoz tartozott; átvinni annyi volna, mint felkínálni egy
      // másik cég tételét is.
      setCandidate(null);
    },
    [setCandidate]
  );

  /**
   * AHOL A KURZOR ÁLL — a felület szerint.
   *
   * A roving tabindex célja az első sor, amíg a könyvelő nem lépett: így a
   * `Tab` a listába érkezik, nem az első sor MÖGÉ. Ezért van alapértelmezve,
   * és ezért kell a léptetésnek is EBBŐL indulnia.
   */
  /**
   * A KURZOR MINDIG LÁTHATÓ SORON ÁLL.
   *
   * Ha a szűrés kivitte alóla a sort, az elsőre lépünk — nem hagyjuk egy
   * elrejtett bizonylaton. Enélkül a részletpanel egy olyan papírt mutatna,
   * ami a kupacban nincs ott, és az `Enter` azt fűzné le.
   */
  const current = focusedId !== null && order.includes(focusedId) ? focusedId : (order[0] ?? null);

  /** A lista VÉGÉN MEGÁLL, nem fordul körbe. */
  const move = useCallback(
    (step: number) => {
      if (order.length === 0) {
        return;
      }
      // A LÉPTETÉS ONNAN INDUL, AHOL A KURZOR LÁTSZIK.
      //
      // A nyers `focusedId` a könyvelő első leütéséig `null`, a felület
      // viszont MÁR AZ ELSŐ SORT jelöli (a roving tabindex célja az, hogy a
      // `Tab` a listába érkezzen, ne mögé). A kettőt szétcsúsztatva az első
      // `J` vagy `↓` a `-1 → 0` lépést tenné meg — vagyis LÁTHATÓAN SEMMIT,
      // és a könyvelőnek kétszer kellene megnyomnia. Billentyűzet-első
      // felületen ez pont az a súrlódás, amiért a D9 megszületett.
      // *(A kiosztás tesztje fogta meg, 09-03.)*
      const at = current === null ? -1 : order.indexOf(current);
      const next = order[Math.min(Math.max(at + step, 0), order.length - 1)];

      selectDocument(next);
      rows.current.get(next)?.focus();
    },
    [order, current, selectDocument]
  );

  /**
   * A VISSZAGÖRGETÉS — közös ág a bukásnak és az újrapróbálás bukásának.
   *
   * A fókusz CSAK AKKOR ugrik vissza, ha ez az EGYETLEN elintézetlen bukás.
   * A 2.6/22 második pontja szerint a valódi bukás „ritkán sok sor egyszerre";
   * nyolc egymás után beérkező hibaválasz nyolcszor rántaná el a kurzort a
   * könyvelő alól, épp amikor az elsővel próbál foglalkozni. A többit a
   * sáv gyűjti össze, és a `focusFailed` visz oda — a bukás így sem
   * mulasztható el, de nem is kapkod.
   */
  const rollBack = useCallback((entry: FailedFiling) => {
    setList((previous) => (previous ? restoreItem(previous, entry.item) : previous));

    // A 409 UTÁN A TÉTEL LEZÁRTNAK SZÁMÍT. A szerver épp azt mondta, hogy
    // időközben megváltozott — visszatéve a jelöltek közé a könyvelő megint
    // rábökne, és a második `Enter` megint 409-cel bukna. Pont az a hurok,
    // ami ellen a két külön ok van. A hálózati bukásnál viszont KI KELL
    // VENNI: ott a mentés meg sem történt, a tétel változatlanul nyitott.
    // *(Code review fogta meg, 09-03.)*
    if (entry.reason !== 'conflict') {
      setSettled((previous) => {
        const next = new Set(previous);
        next.delete(entry.discrepancyId);
        return next;
      });
    }

    // AZ UPDATER TISZTA MARAD. A fókuszt egy effekt teszi rá (lásd lent) —
    // a React újrafuttathatja a reducert, és egy benne elrejtett mellékhatás
    // kétszer futna le.
    setFailed((previous) => [
      ...previous.filter((one) => one.documentId !== entry.documentId),
      entry,
    ]);
  }, []);

  /**
   * A HÁTTÉRMENTÉS. A `documentId` és a `discrepancyId` a záradékban utazik,
   * nem az állapotból olvassuk vissza — mire a válasz megjön, a kurzor rég
   * továbbment, és az állapotból vett azonosító MÁSIK sorra mutatna.
   */
  const save = useCallback(
    (item: WorklistItem, discrepancyId: string) => {
      setPending((previous) => [...previous, item.id]);

      void api<FilingResult>(`/discrepancies/${discrepancyId}/file`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ documentId: item.id }),
      })
        .then(() => {
          setPending((previous) => previous.filter((one) => one !== item.id));
        })
        .catch((caught: unknown) => {
          setPending((previous) => previous.filter((one) => one !== item.id));

          const failedWith = caught instanceof ApiError ? caught.status : 0;

          if (failedWith === 401) {
            signOut('expired');
            return;
          }

          rollBack({
            documentId: item.id,
            discrepancyId,
            item,
            reason: failedWith === 409 ? 'conflict' : 'failed',
          });
        });
    },
    [rollBack, signOut]
  );

  /**
   * `Enter` — MENTÉS ÉS KÖVETKEZŐ.
   *
   * A sorrend nem cserélhető fel: előbb az előellenőrzés, aztán a
   * továbblépés, és CSAK AZUTÁN a hálózat. Egy megfordított sorrendben a
   * kurzor akkor is elmozdulna, amikor a felület már tudja, hogy a mentés
   * bukni fog.
   */
  const fileAndAdvance = useCallback(() => {
    if (!(current && candidate && list)) {
      return;
    }

    const item = list.items.find((one) => one.id === current);
    if (!item) {
      return;
    }

    // 1. ELŐELLENŐRZÉS. Nem lépünk tovább, és nem is hívunk hálózatot.
    const refused = blockFor(candidate);
    if (refused) {
      setBlock(refused);
      return;
    }

    // 2. A SOR ELTŰNIK, ÉS A KURZOR TOVÁBBLÉP — még a kérés elindítása előtt.
    const next = idAfterRemoving(order, current);
    setList((previous) => (previous ? removeItem(previous, current) : previous));
    setSettled((previous) => new Set(previous).add(candidate.id));
    setCandidate(null);
    setFocusedId(next);
    if (next) {
      rows.current.get(next)?.focus();
    }

    // 3. A MENTÉS A HÁTTÉRBEN.
    save(item, candidate.id);
  }, [current, candidate, list, order, save, setCandidate]);

  /**
   * A KIZÁRÁS ELKÜLDÉSE — az ablak leteltével.
   *
   * A hibaága UGYANAZ a sáv, mint a mentésé: a sor visszajön, megjelölve. A
   * kizárás is „elveszett munka", ha nem ért célba, és két külön hibafelület
   * két helyen tanítaná meg a könyvelőt figyelni.
   */
  const sendExclusion = useCallback(
    (entry: PendingExclusion) => {
      void api(`/documents/${entry.documentId}/not-deductible`, { method: 'POST' })
        .then(() => undefined)
        .catch((caught: unknown) => {
          const failedWith = caught instanceof ApiError ? caught.status : 0;

          if (failedWith === 401) {
            signOut('expired');
            return;
          }

          rollBack({
            documentId: entry.documentId,
            // A KIZÁRÁSHOZ NINCS HIÁNYTÉTEL. A `discrepancyId` üres marad, és
            // a visszagörgetés `settled`-ága ezért nem is nyúl semmihez —
            // a mező a közös alakért van, nem mert volna jelentése.
            discrepancyId: '',
            item: entry.item,
            reason: failedWith === 409 ? 'conflict' : 'failed',
          });
        });
    },
    [rollBack, signOut]
  );

  /**
   * AZ IDŐZÍTŐ — a legközelebbi lejáratra, nem másodpercenként.
   *
   * Egy másodperces körökben futó `setInterval` minden ébredésnél
   * újrarenderelné a listát, akkor is, amikor nincs mit tenni. Így viszont a
   * visszaszámláló SEM frissülne a sávban — azt a `CountdownBand` saját,
   * a sáv élettartamára korlátozott ütemezője viszi (`Worklist.tsx`).
   */
  useEffect(() => {
    if (excluding.length === 0) {
      return;
    }

    const soonest = Math.min(...excluding.map((one) => one.dueAt));
    const timer = setTimeout(
      () => {
        const now = Date.now();
        const due = dueNow(excluding, now);
        setExcluding(stillWaiting(excluding, now));
        for (const entry of due) {
          sendExclusion(entry);
        }
      },
      Math.max(soonest - Date.now(), 0)
    );

    return () => clearTimeout(timer);
  }, [excluding, sendExclusion]);

  const retryFailed = useCallback(
    (documentId: string) => {
      const entry = failed.find((one) => one.documentId === documentId);
      if (!entry) {
        return;
      }

      // A SOR ÚJRA ELTŰNIK, ahogy az első `Enter`-nél — a bukott sáv nem
      // maradhat ott egy futó újrapróbálás mellett, mert akkor a könyvelő
      // kétszer nyomná meg.
      setFailed((previous) => previous.filter((one) => one.documentId !== documentId));
      setList((previous) => (previous ? removeItem(previous, documentId) : previous));
      setSettled((previous) => new Set(previous).add(entry.discrepancyId));
      save(entry.item, entry.discrepancyId);
    },
    [failed, save]
  );

  /**
   * ELVETEM — a sor a kupacban marad, a bukás jelölése eltűnik.
   *
   * Ez NEM a mentés visszavonása: a szerver felé már nincs mit visszavonni,
   * a mentés elbukott. Azt mondja ki, hogy a könyvelő TUDOMÁSUL VETTE, és
   * később foglalkozik vele — enélkül a 3. pont zárja („nem léphet ki")
   * megkerülhetetlen csapdává válna egy tartósan hibás soron.
   */
  const discardFailed = useCallback((documentId: string) => {
    setFailed((previous) => previous.filter((one) => one.documentId !== documentId));
  }, []);

  /**
   * A FÓKUSZ VISSZAUGRIK A BUKOTT SORRA — KÖTEGENKÉNT EGYSZER.
   *
   * Effekt, nem ref-visszahívás: a sor akkor is a helyén lehet, amikor a
   * visszagörgetés nem mountolta újra (egy közben lefutott `reload` már
   * visszahozhatta). A mountra kötött fókusz ilyenkor NÉMÁN elmaradt volna —
   * a `rows` térképből viszont mindkét úton elérjük.
   *
   * A `failedBefore` az ÁTMENETET méri: „nem volt bukás" → „van". A kötegen
   * BELÜL nem mozdulunk többet, mert a 2.6/22 második pontja szerint a valódi
   * bukás „ritkán sok sor egyszerre", és nyolc egymás utáni rántás kivenné a
   * kurzort a könyvelő kezéből, épp amikor az elsővel foglalkozik. A
   * többihez a sáv „Ugrás az elsőre" gombja visz.
   *
   * ⚠️ A FELTÉTEL `> 0` ÉS NEM `=== 1`, és ez MÉRÉS eredménye, nem ízlés.
   * A `=== 1` alak csak akkor ugratott, ha a bukások EGYESÉVEL csordogáltak
   * be. A React 18 viszont egy renderbe vonja a közel egyszerre érkező
   * válaszokat, tehát egy hálózati kiesésnél a `failed` nem 0→1→2-t lép,
   * hanem 0→2-t — és a fókusz olyankor SEHOVA nem ment. Pont abban az
   * esetben maradt el, amit a 2.6/22 külön nevesít („egy három másodperces
   * kiesés nyolc-tíz sort visz magával"). Megmérve a `Worklist.spec.tsx`
   * „egyidejű bukásnál is visszaugrik" tesztjével, ami a `=== 1` alakon
   * bukik. *(Viktor döntése, 2026-09-04: kötegenként egy ugrás.)*
   *
   * A `failed[0]` a köteg ELSŐ eleme: `before === 0` mellett a tömb MAGA a
   * köteg, tehát nincs mit külön kikeresni.
   */
  useEffect(() => {
    const before = failedBefore.current;
    failedBefore.current = failed.length;

    if (before === 0 && failed.length > 0) {
      rows.current.get(failed[0].documentId)?.focus();
    }
  }, [failed]);

  /**
   * A LEZÁRT TÉTELEK NYILVÁNTARTÁSA CSAK A FRISS HIÁNYLISTÁIG ÉL.
   *
   * A `settled` azért van, mert a hiánylistát a sor-mód alatt nem töltjük
   * újra. Amint mégis megtesszük, az IGAZSÁG a szerverről jön, és a helyi
   * nyilvántartás nemcsak fölösleges, hanem káros: egy szerveroldalon
   * újranyitott tétel örökre rejtve maradna a jelöltek közül.
   */
  const clearSettled = useCallback(() => setSettled(new Set()), []);

  /**
   * `X` — NEM ELSZÁMOLHATÓ (D9).
   *
   * A sor AZONNAL eltűnik és a kurzor továbblép — ugyanaz a mozdulat, mint
   * az `Enter`-nél, mert a könyvelő számára is ugyanaz: „ezzel végeztem".
   * A kérés viszont NEM indul el: hat másodpercig visszavonható.
   *
   * NINCS HIÁNYTÉTEL-FELTÉTEL, az `Enter`-rel ellentétben. A parkolójegyhez
   * nem tartozik NAV-bejelentés, és épp ezért kell kizárni — egy „előbb
   * válassz hiánytételt" feltétel pont azokra a papírokra állna, amikre a
   * művelet való.
   */
  const exclude = useCallback(() => {
    if (!(current && list)) {
      return;
    }

    const item = list.items.find((one) => one.id === current);
    if (!item) {
      return;
    }

    const next = idAfterRemoving(order, current);
    setList((previous) => (previous ? removeItem(previous, current) : previous));
    setCandidate(null);
    setFocusedId(next);
    if (next) {
      rows.current.get(next)?.focus();
    }

    setExcluding((previous) => [
      ...previous,
      { documentId: current, item, dueAt: Date.now() + UNDO_WINDOW_MS },
    ]);
  }, [current, list, order, setCandidate]);

  /**
   * A VISSZAVONÁS — a sor visszajön, és a szerver soha nem tudott róla.
   *
   * Ezért nincs mit „visszacsinálni": a művelet el sem indult. Ez a
   * választott alak lényege — a napló csak a valódi döntéseket kapja meg.
   */
  const undoExclusion = useCallback((documentId: string) => {
    setExcluding((previous) => {
      const entry = previous.find((one) => one.documentId === documentId);
      if (entry) {
        setList((list) => (list ? restoreItem(list, entry.item) : list));
      }
      return previous.filter((one) => one.documentId !== documentId);
    });
  }, []);

  const focusFailed = useCallback(() => {
    const first = failed[0];
    if (!first) {
      return;
    }
    setFocusedId(first.documentId);
    setCandidate(null);
    rows.current.get(first.documentId)?.focus();
  }, [failed, setCandidate]);

  /**
   * A KILÉPÉS ZÁRJA — a 2.6/22 harmadik pontja a böngésző szintjén is.
   *
   * A futó mentés is számít, nem csak a bukott: egy lap-bezárás a levegőben
   * lévő kérés válaszát viszi magával, és a könyvelő soha nem tudná meg,
   * hogy az a nyolc sor lefűződött-e.
   */
  const blocked = failed.length > 0;

  useEffect(() => {
    // A VISSZAVONÁSI ABLAK IS SZÁMÍT. Az `exclusion.ts` szerint a lap
    // bezárása az ablakon belül azt jelenti, hogy a kizárás NEM történik meg
    // — ez a biztonságos alapértelmezés, de a könyvelőnek tudnia kell róla,
    // mielőtt elmegy. Enélkül azt hinné, hogy elintézte azt a nyolc papírt.
    if (!(blocked || pending.length > 0 || excluding.length > 0)) {
      return;
    }

    function onBeforeUnload(event: BeforeUnloadEvent) {
      event.preventDefault();
    }

    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [blocked, pending.length, excluding.length]);

  /**
   * A RÖVIDÍTÉSEK CSAK AKKOR ÉLNEK, HA VAN MIT CSINÁLNI — a `useShortcuts`
   * minden találatnál `preventDefault()`-ot hív, még a `run()` előtt. Egy
   * üres listán bejegyzett nyíl elvenné a lapgörgetést anélkül, hogy bármit
   * tenne.
   */
  const shortcuts = useMemo<Shortcut[]>(() => {
    const keys: Shortcut[] = [];

    if (order.length > 0) {
      keys.push(
        { key: 'ArrowDown', labelKey: 'shortcut.nextDocument', run: () => move(1) },
        { key: 'ArrowUp', labelKey: 'shortcut.prevDocument', run: () => move(-1) },
        // A `J`/`K` A D9 KIOSZTÁSA, a nyilak mellett — nem helyettük. A
        // könyvelő a Tensoftból jön, ahol a kéz az alapsoron marad; a nyíl
        // viszont az, amit egy új felhasználó ELŐSZÖR megnyom. Mindkettő
        // bejegyezve a súgóban is látszik, tehát a paletta és a súgó
        // TANÍTJA a gyorsabb utat.
        { key: 'j', labelKey: 'shortcut.nextDocument', run: () => move(1) },
        { key: 'k', labelKey: 'shortcut.prevDocument', run: () => move(-1) }
      );
    }

    // AZ `Enter` CSAK KIJELÖLT HIÁNYTÉTELLEL. Enélkül a könyvelő azt hinné,
    // hogy a lefűzés „nem működik", holott csak nem mondta meg, MIT zár le.
    if (current && candidate) {
      keys.push({ key: 'Enter', labelKey: 'shortcut.fileAndNext', run: fileAndAdvance });
    }

    // AZ `X` HIÁNYTÉTEL NÉLKÜL IS ÉL — csak bizonylat kell hozzá. A D9
    // kiosztásában az `X` egyetlen dolgot jelent az egész rendszerben:
    // „kizárás a normál menetből, naplózva".
    if (current) {
      keys.push({ key: 'x', labelKey: 'shortcut.exclude', run: exclude });
      // A `→` BELÉP A BIZONYLATBA (#68) — a fókusz a papírra. Az `Enter`
      // MARAD LEFŰZÉS: az a sor-mód szíve, és ha belépne, minden lefűzés két
      // leütés lenne.
      keys.push({
        key: 'ArrowRight',
        labelKey: 'shortcut.enterDocument',
        run: () => setEnterRequest({}),
      });
    }

    // A KERESÉS AKKOR IS ÉL, HA A SZŰRŐ ÜRESRE FUTOTT. A `/` a mezőbe visz,
    // nem a találatokra — nulla találatnál épp az kell, hogy át lehessen
    // írni a keresést.
    keys.push({ key: '/', labelKey: 'shortcut.search', run: () => setSearchRequest({}) });

    return keys;
  }, [move, order.length, current, candidate, fileAndAdvance, exclude]);

  /**
   * AZ `Esc` A KERESŐBEN TÖRLI A SZŰRŐT, és visszavisz a kupacra.
   *
   * A mező mindig látszik (#68), tehát egy kitöltve hagyott szűrő már nem
   * rejt el LÁTHATATLANUL sorokat — de az `Esc` jelentése az egész
   * felületen „kilépés", itt a szűrőből is. A fókusz a kijelölt sorra megy,
   * nem a semmibe: onnan folytatódik a `J`/`K` és az `Enter`.
   */
  // A `current`, nem a `focusedId`: első betöltés után még senki nem
  // kattintott, a kijelölt sor ilyenkor a lista első LÁTHATÓ sora.
  const focusQueue = useCallback(() => {
    if (current !== null) {
      rows.current.get(current)?.focus();
    }
  }, [current]);

  const leaveSearch = useCallback(() => {
    setQuery('');
    focusQueue();
  }, [focusQueue]);

  return {
    status,
    list,
    loadedSince,
    visible,
    query,
    setQuery,
    searchRequest,
    leaveSearch,
    enterRequest,
    focusQueue,
    reload: load,
    refresh,
    focusedId: current,
    selectDocument,
    registerRow,
    candidate,
    candidateId: candidate?.id ?? null,
    setCandidate,
    settledDiscrepancyIds: settled,
    block,
    pendingCount: pending.length,
    failed,
    blocked,
    fileAndAdvance,
    exclude,
    pendingExclusions: excluding,
    undoExclusion,
    retryFailed,
    discardFailed,
    focusFailed,
    clearSettled,
    shortcuts,
  };
}

function isUnauthorized(caught: unknown): boolean {
  return caught instanceof ApiError && caught.status === 401;
}

/** A listakérés hibája: a 401 kiléptet, a 403 zsákutca, minden más újrapróbálható. */
function failureOf(caught: unknown): 'expired' | 'forbidden' | 'failed' {
  const failedWith = caught instanceof ApiError ? caught.status : 0;
  if (failedWith === 401) return 'expired';
  return failedWith === 403 ? 'forbidden' : 'failed';
}
